import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { ThemeProvider, CssBaseline, createTheme } from '@mui/material';
import { FaTimes } from 'react-icons/fa';
import { Drawer, IconButton, List, ListItem, ListItemText, Box, ListItemIcon } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import useMediaQuery from '@mui/material/useMediaQuery';
import HomeIcon from '@mui/icons-material/Home';
import LoginIcon from '@mui/icons-material/Login';
import StarIcon from '@mui/icons-material/Star';
import SchoolIcon from '@mui/icons-material/School';
import LogoutIcon from '@mui/icons-material/Logout';
import DashboardIcon from '@mui/icons-material/Dashboard';
import Login from './pages/Login';
import Register from './pages/Register';
import Practice from './pages/Practice';
import QuestionPage from './pages/QuestionPage';
import Dashboard from './pages/Dashboard';

function Home() {
  const isMobile = window.innerWidth <= 768;

  return (
    <>
      <style>
        {`
          @keyframes gradientShift {
            0%, 100% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
          }

          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
          }

          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }

          @keyframes slideInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>

      {/* Hero Section with Full-Screen Gradient Background */}
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
        backgroundSize: '400% 400%',
        animation: 'gradientShift 15s ease infinite',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isMobile ? '20px' : '40px'
      }}>
        {/* Animated Background Elements */}
        <div style={{
          position: 'absolute',
          top: '10%',
          left: '10%',
          width: '100px',
          height: '100px',
          background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
          borderRadius: '50%',
          animation: 'float 6s ease-in-out infinite',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,255,255,0.2)'
        }}></div>

        <div style={{
          position: 'absolute',
          bottom: '20%',
          right: '15%',
          width: '150px',
          height: '150px',
          background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)',
          borderRadius: '50%',
          animation: 'float 8s ease-in-out infinite reverse',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,255,255,0.15)'
        }}></div>

        <div style={{
          position: 'absolute',
          top: '60%',
          left: '20%',
          width: '80px',
          height: '80px',
          background: 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)',
          borderRadius: '50%',
          animation: 'pulse 4s ease-in-out infinite',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,255,255,0.1)'
        }}></div>

        {/* Main Content Container */}
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'center',
          justifyContent: 'center',
          maxWidth: '1200px',
          width: '100%',
          gap: isMobile ? 0 : 60,
          zIndex: 10
        }}>
          {/* Illustration with Enhanced Styling - Hidden on Mobile */}
          {!isMobile && (
            <div style={{
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              maxWidth: '50%',
              animation: 'slideInUp 1s ease-out'
            }}>
              <div style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 100%)',
                borderRadius: 24,
                padding: 32,
                boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.3)',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: 'linear-gradient(90deg, #667eea, #764ba2, #f093fb, #667eea)',
                  backgroundSize: '200% 100%',
                  animation: 'gradientShift 3s ease infinite'
                }}></div>

                <div style={{
                  width: '100%',
                  maxWidth: 380,
                  height: 380,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 120,
                  filter: 'drop-shadow(0 8px 24px rgba(0,0,0,0.1))'
                }}>
                  📚
                </div>
              </div>
            </div>
          )}

          {/* Enhanced Text Content */}
          <div style={{
            flex: isMobile ? 'none' : 1,
            textAlign: 'center',
            maxWidth: isMobile ? '100%' : '50%',
            width: isMobile ? '100%' : 'auto',
            animation: 'slideInUp 1s ease-out 0.2s both'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 100%)',
              borderRadius: 24,
              padding: isMobile ? 24 : 40,
              boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255,255,255,0.3)',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: 'linear-gradient(90deg, #667eea, #764ba2, #f093fb, #667eea)',
                backgroundSize: '200% 100%',
                animation: 'gradientShift 3s ease infinite'
              }}></div>

              <h1 style={{
                fontSize: isMobile ? 32 : 48,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                marginBottom: 20,
                direction: 'rtl',
                fontWeight: 'bold',
                textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                lineHeight: 1.2
              }}>
               شايل همّ امتحان أمير؟ إحنا بنسهّلها <span style={{
                 textShadow: 'none',
                 WebkitBackgroundClip: 'initial',
                 WebkitTextFillColor: 'initial',
                 backgroundClip: 'initial',
                 color: '#667eea',
                 display: 'inline-block',
                 verticalAlign: 'middle'
               }}>😉</span>
              </h1>

              <p style={{
                fontSize: isMobile ? 18 : 22,
                color: '#4a5568',
                maxWidth: 500,
                textAlign: 'center',
                marginBottom: 32,
                direction: 'rtl',
                margin: '0 auto 32px auto',
                lineHeight: 1.6,
                fontWeight: '500'
              }}>
حولنا الدراسة للعبة! تدرب، نافس حالك، وشوف تقدمك بيزيد يوم بعد يوم. كل الأدوات اللي بتحتاجها لتهكّر امتحان أمير موجودة هون
                <br />
بلّش التحدي الآن 👇
              </p>

              {/* Enhanced CTA Button */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}>
                <Link to="/login" style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: '#fff',
                  padding: isMobile ? '16px 36px' : '18px 48px',
                  borderRadius: 16,
                  fontSize: isMobile ? 18 : 20,
                  textDecoration: 'none',
                  fontWeight: 'bold',
                  boxShadow: '0 8px 32px rgba(102, 126, 234, 0.4)',
                  transition: 'all 0.3s ease',
                  direction: 'rtl',
                  display: 'inline-block',
                  position: 'relative',
                  overflow: 'hidden',
                  border: '2px solid transparent'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 12px 40px rgba(102, 126, 234, 0.6)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 8px 32px rgba(102, 126, 234, 0.4)';
                }}>
                  <span style={{ position: 'relative', zIndex: 2 }}>
                    🚀 ابدأ الآن
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function Navigation({ user }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isMobile = useMediaQuery('(max-width:768px)');
  const location = useLocation();
  const isHome = location.pathname === '/';

  const handleLogout = () => {
    window.location.href = process.env.REACT_APP_API_URL + '/auth/logout';
  };

  // Debug: Log user state and menu items
  console.log('[Dashboard Debug] User:', user);
  console.log('[Dashboard Debug] User exists?', !!user);

  const menuItems = user ? [
    { text: 'الرئيسية', path: '/', icon: <HomeIcon /> },
    { text: 'لوحة التحكم', path: '/dashboard', icon: <DashboardIcon /> },
    { text: 'التمارين', path: '/practice', icon: <SchoolIcon /> },
    { text: 'الاشتراك', path: '/premium', icon: <StarIcon /> },
    { text: 'تسجيل الخروج', action: handleLogout, icon: <LogoutIcon /> }
  ] : [
    { text: 'الرئيسية', path: '/', icon: <HomeIcon /> },
    { text: 'تسجيل الدخول', path: '/login', icon: <LoginIcon /> },
    { text: 'التمارين', path: '/practice', icon: <SchoolIcon /> },
    { text: 'الاشتراك', path: '/premium', icon: <StarIcon /> }
  ];

  console.log('[Dashboard Debug] Menu items:', menuItems.map(m => m.text));

  return (
    <>
      <style>
        {`
          @keyframes slideDown {
            from {
              opacity: 0;
              transform: translateY(-20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
      <div style={{
        position: isHome ? 'absolute' : 'relative',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        background: isHome ? 'linear-gradient(180deg, rgba(0,0,0,0.2) 0%, transparent 100%)' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        boxShadow: !isHome ? '0 4px 24px rgba(0,0,0,0.1)' : 'none',
        backdropFilter: isHome ? 'blur(10px)' : 'none',
        animation: 'slideDown 0.5s ease-out'
      }}>
        <div style={{
          maxWidth: 1200,
          margin: '0 auto',
          padding: isMobile ? '12px 20px' : '16px 40px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Link to="/" style={{
            fontSize: isMobile ? 20 : 24,
            fontWeight: 'bold',
            color: '#fff',
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            textShadow: '0 2px 4px rgba(0,0,0,0.2)',
            direction: 'rtl'
          }}>
            📚 أمير ماستر
          </Link>

          {isMobile ? (
            <>
              <IconButton
                onClick={() => setDrawerOpen(true)}
                style={{ color: '#fff' }}
              >
                <MenuIcon />
              </IconButton>
              <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                PaperProps={{
                  style: {
                    width: 250,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                  }
                }}
              >
                <Box style={{ padding: '20px', direction: 'rtl' }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 20
                  }}>
                    <span style={{ fontSize: 20, fontWeight: 'bold', color: '#fff' }}>القائمة</span>
                    <IconButton onClick={() => setDrawerOpen(false)} style={{ color: '#fff' }}>
                      <FaTimes />
                    </IconButton>
                  </div>
                  <List>
                    {menuItems.map((item) => (
                      <ListItem
                        button
                        key={item.text}
                        component={item.action ? 'div' : Link}
                        to={item.path}
                        onClick={() => {
                          if (item.action) {
                            item.action();
                          }
                          setDrawerOpen(false);
                        }}
                        style={{
                          color: '#fff',
                          borderRadius: 8,
                          marginBottom: 8,
                          background: location.pathname === item.path ? 'rgba(255,255,255,0.2)' : 'transparent',
                          cursor: 'pointer'
                        }}
                      >
                        <ListItemIcon style={{ color: '#fff', minWidth: 40 }}>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText primary={item.text} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Drawer>
            </>
          ) : (
            <div style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
              {menuItems.map((item) => (
                item.action ? (
                  <div
                    key={item.text}
                    onClick={item.action}
                    style={{
                      color: '#fff',
                      textDecoration: 'none',
                      fontWeight: '500',
                      fontSize: 16,
                      padding: '8px 16px',
                      borderRadius: 8,
                      background: 'transparent',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    {item.text}
                  </div>
                ) : (
                  <Link
                    key={item.text}
                    to={item.path}
                    style={{
                      color: '#fff',
                      textDecoration: 'none',
                      fontWeight: '500',
                      fontSize: 16,
                      padding: '8px 16px',
                      borderRadius: 8,
                      background: location.pathname === item.path ? 'rgba(255,255,255,0.2)' : 'transparent',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6
                    }}
                    onMouseEnter={(e) => {
                      if (location.pathname !== item.path) {
                        e.target.style.background = 'rgba(255,255,255,0.1)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (location.pathname !== item.path) {
                        e.target.style.background = 'transparent';
                      }
                    }}
                  >
                    {item.text}
                  </Link>
                )
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

const theme = createTheme({
  direction: 'rtl',
  typography: {
    fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif'
  }
});

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch current user from backend
    const fetchUser = async () => {
      try {
        const response = await fetch(process.env.REACT_APP_API_URL + '/api/user', {
          credentials: 'include'
        });
        const data = await response.json();
        setUser(data.user);
      } catch (error) {
        console.error('Error fetching user:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, []);

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: '#fff',
          fontSize: 24,
          direction: 'rtl'
        }}>
          جارٍ التحميل...
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Navigation user={user} />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/practice" element={<Practice />} />
          <Route path="/practice/question/:id" element={<QuestionPage />} />
          <Route path="/premium" element={<Register />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;
